<template>
  <Dialog
    v-model:visible="showDialog"
    :modal="true"
    :closable="false"
    :style="{ width: '500px', zIndex: 1100 }"
    :header="t('wkt.client.common.dialog.select-date.title')"
  >
    <div class="dialog-content">
      <div class="date-time-row">
        <div class="field-col">
          <label class="field-label">
            {{ t("wkt.client.common.dialog.select-date.date") }}
          </label>
          <Calendar
            v-model="selectedDate"
            dateFormat="dd/mm/yy"
            :minDate="minDate"
            :firstDayOfWeek="1"
            :locale="calendarLocale"
            showIcon
            iconDisplay="input"
            :class="{ 'p-invalid': !isDateValid }"
          />
          <small v-if="!isDateValid" class="p-error">
            {{ t("wkt.client.common.validation-utils.required_field") }}
          </small>
        </div>
        <div class="field-col">
          <label class="field-label">
            {{ t("wkt.client.common.dialog.select-date.time") }}
          </label>
          <Calendar
            v-model="selectedTime"
            timeOnly
            hourFormat="24"
            showIcon
            iconDisplay="input"
            :class="{ 'p-invalid': !isTimeValid }"
          />
          <small v-if="!isTimeValid" class="p-error">
            {{ t("wkt.client.common.validation-utils.required_field") }}
          </small>
        </div>
      </div>
    </div>
    <template #footer>
      <Button
        class="footer-primary-button"
        :label="t('wkt.client.common.dialog-buttons.ok')"
        color="#185EA9"
        variant="text"
        @click="doOk"
      />
      <Button
        class="uppercase"
        :label="t('wkt.client.common.dialog-buttons.cancel')"
        severity="secondary"
        text
        @click="close"
      />
    </template>
  </Dialog>
</template>
<script setup lang="ts">
import { useDateFormatter } from "@wkt/composables/useDateFormatter";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
// Composables
const { t, locale } = useI18n();
const { formatLongDateTime } = useDateFormatter();
// State
const showDialog = ref(false);
const selectedDate = ref<Date | null>(null);
const selectedTime = ref<Date | null>(null);
const minDate = ref<Date | undefined>(undefined);
const msgCbOk = ref<((date: Date) => void) | null>(null);
const isDateValid = ref(true);
const isTimeValid = ref(true);
// Computed
const calendarLocale = computed(() => {
  // PrimeVue usa códigos de locale estándar
  return locale.value || "es";
});
// Methods
function isVisible(): boolean {
  return showDialog.value;
}
function close() {
  showDialog.value = false;
}
function open(
  date?: Date | string,
  minDateParam?: Date | string,
  callback?: (date: Date) => void
) {
  // Reset validation
  isDateValid.value = true;
  isTimeValid.value = true;
  // Set min date
  if (minDateParam) {
    minDate.value =
      typeof minDateParam === "string" ? new Date(minDateParam) : minDateParam;
  } else {
    minDate.value = undefined;
  }
  // Set date and time
  let dateToSet: Date;
  if (!date) {
    dateToSet = new Date();
    dateToSet.setHours(12, 0, 0, 0);
  } else if (typeof date === "string") {
    const formatted = formatLongDateTime(date);
    dateToSet = parseDateTimeString(formatted);
  } else {
    dateToSet = date;
  }
  selectedDate.value = new Date(dateToSet);
  selectedTime.value = new Date(dateToSet);
  showDialog.value = true;
  msgCbOk.value = callback || null;
}
function doOk() {
  // Validate
  isDateValid.value = !!selectedDate.value;
  isTimeValid.value = !!selectedTime.value;
  if (!isDateValid.value || !isTimeValid.value) {
    return;
  }
  showDialog.value = false;
  if (msgCbOk.value && selectedDate.value && selectedTime.value) {
    // Combinar fecha y hora
    const combinedDate = new Date(selectedDate.value);
    combinedDate.setHours(
      selectedTime.value.getHours(),
      selectedTime.value.getMinutes(),
      0,
      0
    );
    msgCbOk.value(combinedDate);
  }
}
// Helper para parsear string de fecha/hora
function parseDateTimeString(dateTimeStr: string): Date {
  // Formato esperado: "DD/MM/YYYY, HH:MM" o similar
  const parts = dateTimeStr.split(", ");
  if (parts.length >= 2) {
    const datePart = parts[0]; // "DD/MM/YYYY"
    const timePart = parts[1].split(" ")[0]; // "HH:MM"
    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes),
      0,
      0
    );
  }
  return new Date();
}
// Exponer métodos para uso externo
defineExpose({
  open,
  close,
  isVisible,
});
</script>
<style scoped>
.dialog-content {
  padding: 1rem 0;
}
.date-time-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.field-col {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.field-label {
  font-weight: 500;
  color: #495057;
  font-size: 0.9rem;
}
.p-error {
  color: #e24c4c;
  font-size: 0.875rem;
}
:deep(.p-calendar) {
  width: 100%;
}
:deep(.p-inputtext) {
  width: 100%;
}
</style>
